import { Tag, Tree } from "antd";
import type { Key } from "react";
import type { FC } from "react";
import type { Project, SnippetDeploymentTarget } from "../../types";

interface TargetPickerProps {
  projects: Project[];
  value: string[];
  onChange: (environmentIds: string[]) => void;
  /** Existing deployment targets for the snippet being deployed, so already-live environments are marked. Only meaningful for a single snippet — omit when deploying several at once, since their deployment states can differ. */
  deployedTargets?: SnippetDeploymentTarget[];
}

// Groups selectable environments by project. `local` environments are shown disabled rather
// than hidden: the api can never reach a developer's own machine, so they can never be a
// deploy target (see _docs/SNIPPET_COLLECTIONS.md, "Sécurité du déploiement server-side").
//
// Environments without stored WordPress credentials are not filtered out here (that would need
// a credentials check per environment, which isn't available in bulk today) — attempting to
// deploy to one still surfaces a clear per-target failure from the api instead of silently
// succeeding or being hidden.
export const TargetPicker: FC<TargetPickerProps> = ({ projects, value, onChange, deployedTargets }) => {
  const deployedByEnvironmentId = new Map((deployedTargets ?? []).map((target) => [target.environmentId, target]));

  const treeData = projects
    .map((project) => ({
      checkable: false,
      children: project.environments.map((environment) => {
        const deployed = deployedByEnvironmentId.get(environment.id);
        const label =
          environment.name === "local"
            ? `${environment.name} (not reachable from the cloud)`
            : `${environment.name} — ${environment.url}`;

        return {
          disabled: environment.name === "local",
          key: environment.id,
          title: deployed ? (
            <span>
              {label}{" "}
              <Tag color={deployed.upToDate ? "green" : "gold"}>
                {deployed.upToDate ? "Deployed" : "Update available"}
              </Tag>
            </span>
          ) : (
            label
          ),
        };
      }),
      key: `project:${project.id}`,
      selectable: false,
      title: project.name,
    }))
    .filter((node) => node.children.length > 0);

  const handleCheck = (checked: { checked: Key[] } | Key[]) => {
    const keys = Array.isArray(checked) ? checked : checked.checked;
    onChange(keys.filter((key): key is string => typeof key === "string"));
  };

  return (
    <Tree
      checkable
      checkedKeys={value}
      defaultExpandAll
      onCheck={handleCheck}
      treeData={treeData}
    />
  );
};
